import { EDITOR_TEXT_BOX_SEED_HEIGHT_PX } from './editorTextBoxSeedHeight'
import { editorTextBoxConversionForCaseServices } from './editorTextBoxConversionServiceBindings'
import {
  EditorTextBoxConversionError,
  editorTextBoxFromTinyMceItem,
  type EditorTextBoxConversion,
  type EditorTextBoxTinyMceItem,
} from './tinymce-conversion'

/** An insert whose content is TinyMCE HTML: a custom verse or a legacy service template. */
export type EditorTextBoxHtmlInsert = {
  boxId: string
  html: string
  /** Where the box's top-left corner goes: the toolbar seed's origin (`editorTextBoxSeedOrigin`). */
  origin: { x: number; y: number }
  width: number
  zIndex: number
  /** The service rows the case holds; a service block bound to any other row is written unbound. */
  caseServiceIds: ReadonlySet<string>
}

export type EditorTextBoxHtmlInsertResult =
  | { ok: true; conversion: EditorTextBoxConversion }
  | { ok: false; refusal: EditorTextBoxConversionError }

const convertHtml = (
  item: EditorTextBoxTinyMceItem,
  caseServiceIds: ReadonlySet<string>,
): EditorTextBoxHtmlInsertResult => {
  try {
    return {
      ok: true,
      conversion: editorTextBoxConversionForCaseServices(
        editorTextBoxFromTinyMceItem(item),
        caseServiceIds,
      ),
    }
  } catch (error) {
    if (error instanceof EditorTextBoxConversionError)
      return { ok: false, refusal: error }
    throw error
  }
}

/**
 * An HTML insert as a Lotus box, built by the converter that turns a TinyMCE
 * item into a box: placed at the seed origin and started at the seed frame
 * height, so its first paint on the canvas raises the frame to its text as
 * it raises a toolbar seed box's. HTML the converter cannot build returns
 * the converter's refusal unchanged; any other error is thrown.
 */
export const editorTextBoxFromHtmlInsert = ({
  boxId,
  html,
  origin,
  width,
  zIndex,
  caseServiceIds,
}: EditorTextBoxHtmlInsert): EditorTextBoxHtmlInsertResult =>
  convertHtml(
    {
      id: boxId,
      x: origin.x,
      y: origin.y,
      z: zIndex,
      width,
      height: EDITOR_TEXT_BOX_SEED_HEIGHT_PX,
      text: { value: html },
    },
    caseServiceIds,
  )

export type EditorTextBlocksFromHtmlResult =
  | {
      ok: true
      blocks: EditorTextBoxConversion['box']['blocks']
      report: EditorTextBoxConversion['report']
    }
  | { ok: false; refusal: EditorTextBoxConversionError }

/**
 * HTML as the blocks of a box that already exists, such as a legacy service
 * template swapped into a Lotus box: the blocks the converter builds from it,
 * with the report of what did not carry. The blocks do not depend on the
 * frame, so the item converted here has a nominal one. HTML the converter
 * cannot build returns the converter's refusal unchanged.
 */
export const editorTextBlocksFromHtml = (
  html: string,
  caseServiceIds: ReadonlySet<string>,
): EditorTextBlocksFromHtmlResult => {
  const result = convertHtml(
    {
      id: 'html-blocks',
      x: 0,
      y: 0,
      width: 1,
      height: EDITOR_TEXT_BOX_SEED_HEIGHT_PX,
      text: { value: html },
    },
    caseServiceIds,
  )
  if (!result.ok) return result
  return {
    ok: true,
    blocks: result.conversion.box.blocks,
    report: result.conversion.report,
  }
}
